import { useAuth } from "@clerk/clerk-react";
import { Box, IconButton, Paper, TextField, Typography } from "@mui/material";
import { AlertContext } from "contexts/Alert";
import { OrganizationUserContext } from "contexts/Organization";
import {
  InboxSidebarOptionKey,
  OutreachContactsContext,
} from "contexts/OutreachContacts";
import { useContext, useEffect, useRef, useState } from "react";
import { CustomEvent } from "schemas/functions";

import Markdown from "components/Markdown";
import TypingLoader from "features/Home/Sidebar/ChatHelper/TypingLoader";
import { fetcherAuth } from "utils/api";

interface Message {
  role?: string;
  content: string;
  tool_call_id?: string;
  name?: string;
  type?: string;
}

const _INITIAL_MESSAGES = [
  {
    role: "system",
    content: `You are an assistant helping me with sending weekly emails to brands.

You will start the conversation with telling me how many emails you have sent last week, and if you want me to schedule the same number of emails again this week.

Then, for each email that I want to send, call the "schedule_weekly_email" function.

After I schedule emails, you can ask me "Done! 🚀 Interested in sending pitches to any other brands?"
`,
  },
];

const DemoVA = () => {
  const [messages, setMessages] = useState<Message[]>(_INITIAL_MESSAGES);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [typing, setTyping] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState("");
  const chatEndRef = useRef<HTMLDivElement>(null);
  const { getToken } = useAuth();
  const { currentOrg, currentUser } = useContext(OrganizationUserContext);
  const { setErrorAlert } = useContext(AlertContext);
  const { setContacts, currentSidebarOption } = useContext(
    OutreachContactsContext,
  );

  useEffect(() => {
    if (messages.length === 1) {
      displayAssistantMessage(
        `Hey ${currentUser?.name}! 👋 Last week, I scheduled 20 emails for you, and you got 4 (20% response rate!) replies — that's 5x better than average! Want me to schedule another 20 for you this week?`,
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messages, typing]);

  const displayAssistantMessage = (text: string) => {
    setTyping(true);

    const newMessage: Message = { role: "assistant", content: "" };
    setMessages((prev) => [...prev, newMessage]);

    let index = 0;

    const interval = setInterval(() => {
      setMessages((prev) => {
        const updated = [...prev];
        const assistantMsg = { ...updated[updated.length - 1] };

        assistantMsg.content = text.slice(0, index + 1);
        updated[updated.length - 1] = assistantMsg;

        return updated;
      });

      index++;
      if (index >= text.length) {
        clearInterval(interval);
        setTyping(false);
      }
    }, 10);
  };

  const scheduleEmail = async (idx: number) => {
    setLoadingMessage(`Scheduling email #${idx + 1}`);
    try {
      const { outreachContact } = await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg?.id}/demo/schedule/${idx}`,
        "POST",
        {},
      );
      if (currentSidebarOption?.key === InboxSidebarOptionKey.scheduled) {
        setContacts((prev) => [outreachContact, ...prev]);
      }
    } catch (error) {}
  };

  const handleSubmit = async (updatedMessages: Message[]) => {
    if (!input.trim()) return;

    setLoading(true);

    try {
      const { messages } = await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg?.id}/demo/virtual-assistant`,
        "POST",
        {},
        {
          messages: updatedMessages,
        },
      );
      const reply = messages[messages.length - 1];
      if (reply.tool_calls) {
        setMessages(messages);
        let numberOfEmails;
        try {
          numberOfEmails = JSON.parse(
            reply.tool_calls?.[0]?.function?.arguments,
          ).count;
        } catch {
          numberOfEmails = 20;
        }
        setLoadingMessage(`Scheduling ${numberOfEmails} emails`);
        for (let i = 0; i < numberOfEmails; i++) {
          await scheduleEmail(i);
        }
        handleSubmit(messages);
      } else {
        setMessages(messages.slice(0, -1));

        displayAssistantMessage(messages[messages.length - 1].content);
        setLoading(false);
      }
    } catch (error) {
      setErrorAlert(error);
      setLoading(false);
    }
  };

  return (
    <Box display="flex" flexDirection="column" p={2}>
      <Box flexGrow={1} overflow="auto" mb={2}>
        {messages
          .filter((msg) => msg.role === "user" || msg.role === "assistant")
          .map((msg, i) => (
            <Box
              sx={{
                width: "100%",
                display: "flex",
                justifyContent: msg.role === "user" ? "flex-end" : "flex-start",
              }}
              key={i}
            >
              <Paper
                elevation={0}
                sx={{
                  px: msg.role === "user" ? 2 : 0,
                  py: 0.75,
                  my: 1,
                  bgcolor: msg.role === "user" ? "primary.main" : "transparent",
                  whiteSpace: "pre-wrap",
                  borderRadius: "16px",
                }}
              >
                <Markdown
                  content={msg.content}
                  color={msg.role === "user" ? "white" : "inherit"}
                />
              </Paper>
            </Box>
          ))}
        {loading && (
          <Typography
            component="div"
            variant="body2"
            color="text.secondary"
            ml={1}
          >
            <Paper
              elevation={0}
              sx={{
                py: 0.75,
                my: 1,
                bgcolor: "transparent",
                whiteSpace: "pre-wrap",
              }}
            >
              <TypingLoader title={loadingMessage} />
            </Paper>
          </Typography>
        )}
        <div ref={chatEndRef} />
      </Box>

      <Box
        component="form"
        onSubmit={(event: CustomEvent) => {
          event.preventDefault();
          setInput("");
          const userMessage: Message = { role: "user", content: input };
          const updatedMessages = [...messages, userMessage];
          setMessages(updatedMessages);
          setLoadingMessage("");
          handleSubmit(updatedMessages);
        }}
        display="flex"
        gap={1}
      >
        <TextField
          value={input}
          onChange={(e) => setInput(e.target.value)}
          fullWidth
          placeholder="Type your response"
          disabled={loading || typing}
        />
        <IconButton type="submit" disabled={loading || typing || !input.trim()}>
          <Box component="span" className="fa-solid fa-paper-plane-top" />
        </IconButton>
      </Box>
    </Box>
  );
};

export default DemoVA;
